import { MouseEvent, useState } from "react"
import Modal from "@/components/modal"
import Card from "@/components/card"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import physicalRiskStore from "../../model/physical-risk.store"
import { PhysicalRisk } from "../../model/physical-risk.types"
import RiskHeader from "../RiskHeader"
import PhysicalRiskEditModal from "./PhysicalRiskEditModal"
import { STATUS_SAVING } from "@/redux/_status.types"
import { toast } from "react-toastify"
import { isInteractiveTarget } from "../PhysicalRiskSelection"

interface Props {
  riskIds: string[]
  onClose: () => void
  onMerged: () => void
}

export default function PhysicalRiskMergeModal({ riskIds, onClose, onMerged }: Props) {
  const dispatch = useAppDispatch()
  const risks = useAppSelector(physicalRiskStore.selectors.list).filter((risk) => riskIds.includes(risk.id))
  const [targetId, setTargetId] = useState<string | null>(() => risks[0]?.id ?? null)
  const [detail, setDetail] = useState<PhysicalRisk | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  async function merge() {
    if (!targetId || isSaving || risks.length < 2) return
    setIsSaving(true)
    try {
      const response = await dispatch(
        establishmentStore.thunks.mergeRisks({
          riskIds: risks.map((risk) => risk.id),
          targetId
        })
      ).unwrap()
      if (response.error) {
        toast.error(response.error)
        return
      }
      onMerged()
      onClose()
    } catch {
      toast.error("La fusion a échoué. Veuillez réessayer.")
    } finally {
      setIsSaving(false)
    }
  }

  function selectTarget(event: MouseEvent, id: string) {
    if (isInteractiveTarget(event.target as Element)) return
    setTargetId(id)
  }

  return (
    <>
      <Modal
        title="Fusionner les risques sélectionnés"
        isLarge
        isCloseButtonVisible
        onClose={() => {
          if (!isSaving) onClose()
        }}
        onNext={merge}
        onNextCta="Fusionner"
        nextDisabled={isSaving || risks.length < 2 || !risks.some((risk) => risk.id === targetId)}
        status={isSaving ? STATUS_SAVING : undefined}>
        <p className="mb-20">
          Sélectionnez la carte du risque à conserver. Son nom, sa famille et son évaluation seront conservés.
        </p>
        <div className="flex flex-col gap-20">
          {risks.map((risk) => (
            <Card
              key={risk.id}
              isSelected={targetId === risk.id}
              className="mb-0"
              onClick={(event: MouseEvent) => {
                if (!isSaving) selectTarget(event, risk.id)
              }}>
              <RiskHeader
                risk={risk}
                onRiskClick={() => setDetail(risk)}
              />
            </Card>
          ))}
        </div>
      </Modal>
      {detail && <PhysicalRiskEditModal risk={detail} onClose={() => setDetail(null)} />}
    </>
  )
}
